import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PaymentListItem } from '../api/types';
import { useT } from '../i18n';
import { METHODS } from '../lib/doc';
import { dt, money } from '../lib/format';
import { I } from './Icons';
import { SwipeRow } from './SwipeRow';

/** Tabulka plateb: mobil třířádkově, desktop sloupce (layout.css .pay-tbl). */
export function PayTable({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`surface pay-tbl ${className}`.trim()}>
      <div className="th">
        <span />
        <span>Datum</span>
        <span>Plátce</span>
        <span>Způsob</span>
        <span>VS</span>
        <span>Částka</span>
        <span>Doklad</span>
      </div>
      {children}
    </section>
  );
}

export function PayRow({
  payment: p,
  onMatch,
  onSplit,
}: {
  payment: PaymentListItem;
  onMatch?: (p: PaymentListItem) => void;
  onSplit?: (p: PaymentListItem) => void;
}) {
  const t = useT();
  const over = Number(p.unallocated ?? 0) > 0.005 && !!p.documentId;
  const navigate = useNavigate();
  // vrácení (záporná částka) i úhrada přijaté faktury (odchozí) jsou peníze z účtu
  const negative = Number(p.amount) < 0 || p.direction === 'out';
  const actions: { label: string; onClick: () => void; kind?: 'primary' | 'danger' }[] = [];
  if (!p.document && onMatch)
    actions.push({ label: t('row.matchPayment'), kind: 'primary', onClick: () => onMatch(p) });
  if (p.document && onSplit)
    actions.push({
      label: over ? t('row.overpaid') : t('row.split'),
      onClick: () => onSplit(p),
    });
  if (p.document)
    actions.push({
      label: t('row.openDocument'),
      onClick: () => navigate(`/doklady/${p.document?.id}`),
    });
  return (
    <SwipeRow actions={actions}>
      <div className="tr pay-row">
        <span className="c-icon mi">{I[p.method]}</span>
        <span className="c-date">{dt(p.date)}</span>
        <span className="c-name">{p.payerName || '—'}</span>
        <span className="c-method">{METHODS[p.method]}</span>
        <span className="c-vs">{p.variableSymbol ? `VS ${p.variableSymbol}` : ''}</span>
        <span className={`c-amt ${negative ? 'bad' : ''}`.trim()}>
          {negative ? '−' : '+'}
          {money(Math.abs(Number(p.amount)), p.currency ?? 'CZK')}
        </span>
        <span className="c-link">
          {p.document ? (
            <>
              <button
                type="button"
                className="link"
                onClick={() => navigate(`/doklady/${p.document?.id}`)}
              >
                {p.document.number}
                {(p.allocations?.length ?? 0) > 1 ? ` +${(p.allocations?.length ?? 1) - 1}` : ''}
              </button>
              {onSplit && (
                <button
                  type="button"
                  className={`link ${over ? 'warn' : ''}`.trim()}
                  onClick={() => onSplit(p)}
                  title={over ? `Přeplatek ${p.unallocated}` : 'Rozdělit na víc dokladů'}
                >
                  {over ? 'přeplatek' : 'rozdělit'}
                </button>
              )}
            </>
          ) : onMatch ? (
            <button type="button" className="link warn" onClick={() => onMatch(p)}>
              Spárovat
            </button>
          ) : null}
        </span>
      </div>
    </SwipeRow>
  );
}
